"use client";

import { useCallback } from "react";
import { useAppSelector } from "@/store/hooks";
import { validateFields } from "@/lib/utils";
import {
  buildDefaultFieldValues,
  buildSavePayload,
  showErrorToast,
  showSuccessToast,
  showValidationErrorToast,
  prepareSectionPreviewData,
} from "../../form3/utils";
import type {
  FormState,
  CategoryData,
  CategoryItemData,
  SemesterData,
  SubjectData,
  EditingSection,
} from "../types";

interface UseForm7HandlersArgs {
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  variantConfig: any;
  reduxState: Record<string, any> | null;
  pageId?: string;
  pageComponentId?: string;
  selectedSection: any;
  loadAllData: () => Promise<void>;
  onSave?: (data: any) => void;
  componentFieldsConfig: any[];
  semesterFieldsConfig: any[];
  categoryFieldsConfig: any[];
  categoryItemFieldsConfig: any[];
  subjectFieldsConfig: any[];
  electiveFieldsConfig: any[];
}

export function useForm7Handlers({
  formState,
  setFormState,
  variantConfig,
  reduxState,
  pageId,
  pageComponentId,
  selectedSection,
  loadAllData,
  onSave,
  componentFieldsConfig,
  semesterFieldsConfig,
  categoryFieldsConfig,
  categoryItemFieldsConfig,
  subjectFieldsConfig,
  electiveFieldsConfig,
}: UseForm7HandlersArgs) {
  const currentUser = useAppSelector((state) => state.user?.user);

  // ═══════════ FIELD HANDLERS ═══════════
  const handleComponentFieldsUpdate = useCallback((updatedData: any) => {
    setFormState((prev) => ({ ...prev, componentFields: updatedData }));
  }, [setFormState]);

  const handleToolsUpdate = useCallback((tools: any[]) => {
    setFormState((prev) => ({ ...prev, tools }));
  }, [setFormState]);

  const handleSkillsUpdate = useCallback((skills: any[]) => {
    setFormState((prev) => ({ ...prev, skills }));
  }, [setFormState]);

  const handleFormFieldsUpdate = (updatedData: Record<string, any>) => {
    // Merge with existing formFields so that extra fields (semester_id, category_id,
    // category_item_id, electives) set by subject/category handlers are preserved
    // even when FieldsFormatter only returns its own managed field set.
    setFormState((prev) => ({ ...prev, formFields: { ...prev.formFields, ...updatedData } }));
  };

  // ═══════════ CANCEL EDITING ═══════════
  const cancelEditing = () => {
    setFormState((prev) => ({
      ...prev,
      editingSection: null,
      editingId: null,
      editingParentId: null,
      formFields: {},
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  // ═══════════ COMPONENT SAVE ═══════════
  const handleComponentSave = useCallback(async () => {
    if (!variantConfig) { showErrorToast("Configuration not found"); return; }

    const { errors, messages } = validateFields(componentFieldsConfig, formState.componentFields);
    setFormState((prev) => ({ ...prev, componentFieldErrors: errors, componentFieldMessages: messages }));
    if (Object.keys(errors).length > 0) { showValidationErrorToast(); return; }

    try {
      const isUpdate = Boolean(reduxState?.id);
      const payload = {
        ...buildSavePayload(formState.componentFields, pageId, currentUser?.id, isUpdate, {
          variant: selectedSection.variant,
          ...(pageComponentId ? { component_id: Number(pageComponentId) } : {}),
        }),
        tools: formState.tools,
        skills: formState.skills,
      };

      let componentId: any;
      if (isUpdate && reduxState && variantConfig.updateMethod) {
        await variantConfig.updateMethod(Number(reduxState.id), payload);
        componentId = reduxState.id;
        showSuccessToast("Component updated successfully");
      } else if (!isUpdate && variantConfig.createMethod) {
        const response = await variantConfig.createMethod(payload);
        componentId = response?.data?.id || response?.id;
        if (!componentId) throw new Error("Failed to get ID from response");
        showSuccessToast("Component created successfully");
      }

      setFormState((prev) => ({ ...prev, parentId: componentId }));

      if (variantConfig.getMethod && pageComponentId) {
        await loadAllData();
      }

      if (onSave && pageComponentId) {
        onSave(prepareSectionPreviewData(selectedSection, componentId, {
          ...formState.componentFields,
          id: componentId,
          tools: formState.tools,
          skills: formState.skills,
          semesters: formState.semesters,
          subjects: formState.subjects,
        }, formState.semesters, "semesters"));
      }
    } catch (error: any) { showErrorToast(error?.message || "Failed to save"); }
  }, [variantConfig, componentFieldsConfig, formState, reduxState, pageId, pageComponentId, currentUser?.id, selectedSection, loadAllData, onSave, setFormState]);

  // ═══════════ SEMESTER HANDLERS ═══════════
  const handleSemesterClick = (sem: SemesterData) => {
    const fields: Record<string, any> = {};
    semesterFieldsConfig.forEach((f: any) => {
      fields[f.fieldName] = sem[f.fieldName] ?? f.defaultValue ?? "";
    });
    setFormState((prev) => ({
      ...prev,
      editingSection: "semester",
      editingId: sem.id,
      editingParentId: null,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleNewSemester = () => {
    const fields = buildDefaultFieldValues(semesterFieldsConfig);
    // Auto-increment order_index based on existing semesters
    const maxOrder = formState.semesters.reduce(
      (max, sem) => Math.max(max, sem.order_index ?? 0),
      0
    );
    fields.order_index = maxOrder + 1;
    setFormState((prev) => ({
      ...prev,
      editingSection: "semester",
      editingId: null,
      editingParentId: null,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleSemesterSave = async () => {
    const { errors, messages } = validateFields(semesterFieldsConfig, formState.formFields);
    setFormState((prev) => ({ ...prev, formFieldErrors: errors, formFieldMessages: messages }));
    if (Object.keys(errors).length > 0) { showValidationErrorToast(); return; }

    try {
      if (formState.editingId && variantConfig?.updateSemesterMethod) {
        await variantConfig.updateSemesterMethod(formState.editingId, { ...formState.formFields, updated_by: currentUser?.id });
        showSuccessToast("Semester updated");
      } else if (variantConfig?.createSemesterMethod) {
        await variantConfig.createSemesterMethod({
          ...formState.formFields,
          component_id: Number(pageComponentId),
          program_structure_v2_id: formState.parentId,
          created_by: currentUser?.id,
        });
        showSuccessToast("Semester created");
      }
      cancelEditing();
      await loadAllData();
    } catch (error: any) { showErrorToast(error?.message || "Failed to save semester"); }
  };

  const handleSemesterDelete = async (sem: SemesterData) => {
    try {
      if (variantConfig?.deleteSemesterMethod) {
        await variantConfig.deleteSemesterMethod(sem.id);
        showSuccessToast("Semester deleted");
        await loadAllData();
      }
    } catch (error: any) { showErrorToast(error?.message || "Failed to delete semester"); }
  };

  // ═══════════ CATEGORY HANDLERS ═══════════
  const handleCategoryClick = (cat: CategoryData) => {
    const fields: Record<string, any> = {};
    categoryFieldsConfig.forEach((f: any) => {
      fields[f.fieldName] = cat[f.fieldName] ?? f.defaultValue ?? "";
    });
    setFormState((prev) => ({
      ...prev,
      editingSection: "category",
      editingId: cat.id,
      editingParentId: cat.semester_id,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleNewCategory = (semesterId: number) => {
    const fields = buildDefaultFieldValues(categoryFieldsConfig);
    // Auto-increment order_index based on existing categories in this semester
    const semester = formState.semesters.find((s) => s.id === semesterId);
    const maxOrder = (semester?.categories || []).reduce(
      (max, cat) => Math.max(max, cat.order_index ?? 0),
      0
    );
    fields.order_index = maxOrder + 1;
    setFormState((prev) => ({
      ...prev,
      editingSection: "category",
      editingId: null,
      editingParentId: semesterId,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleCategorySave = async () => {
    const { errors, messages } = validateFields(categoryFieldsConfig, formState.formFields);
    setFormState((prev) => ({ ...prev, formFieldErrors: errors, formFieldMessages: messages }));
    if (Object.keys(errors).length > 0) { showValidationErrorToast(); return; }

    try {
      if (formState.editingId && variantConfig?.updateCategoryMethod) {
        await variantConfig.updateCategoryMethod(formState.editingId, { ...formState.formFields, updated_by: currentUser?.id });
        showSuccessToast("Category updated");
      } else if (variantConfig?.createCategoryMethod && formState.editingParentId) {
        await variantConfig.createCategoryMethod({
          ...formState.formFields,
          semester_id: formState.editingParentId,
          created_by: currentUser?.id,
        });
        showSuccessToast("Category created");
      }
      cancelEditing();
      await loadAllData();
    } catch (error: any) { showErrorToast(error?.message || "Failed to save category"); }
  };

  const handleCategoryDelete = async (cat: CategoryData) => {
    try {
      if (variantConfig?.deleteCategoryMethod) {
        await variantConfig.deleteCategoryMethod(cat.id);
        showSuccessToast("Category deleted");
        await loadAllData();
      }
    } catch (error: any) { showErrorToast(error?.message || "Failed to delete category"); }
  };

  // ═══════════ CATEGORY ITEM HANDLERS ═══════════
  const handleCategoryItemClick = (item: CategoryItemData, categoryId: number) => {
    const fields: Record<string, any> = {};
    categoryItemFieldsConfig.forEach((f: any) => {
      fields[f.fieldName] = item[f.fieldName] ?? f.defaultValue ?? "";
    });
    setFormState((prev) => ({
      ...prev,
      editingSection: "categoryItem",
      editingId: item.id,
      editingParentId: categoryId,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleNewCategoryItem = (categoryId: number) => {
    const fields = buildDefaultFieldValues(categoryItemFieldsConfig);
    // Auto-increment priority based on existing items in this category
    let maxPriority = 0;
    formState.semesters.forEach((sem) => {
      (sem.categories || []).forEach((cat) => {
        if (cat.id === categoryId) {
          (cat.items || []).forEach((item) => {
            maxPriority = Math.max(maxPriority, item.priority ?? 0);
          });
        }
      });
    });
    fields.priority = maxPriority + 1;
    setFormState((prev) => ({
      ...prev,
      editingSection: "categoryItem",
      editingId: null,
      editingParentId: categoryId,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleCategoryItemSave = async () => {
    const { errors, messages } = validateFields(categoryItemFieldsConfig, formState.formFields);
    setFormState((prev) => ({ ...prev, formFieldErrors: errors, formFieldMessages: messages }));
    if (Object.keys(errors).length > 0) { showValidationErrorToast(); return; }

    try {
      if (formState.editingId && variantConfig?.updateCategoryItemMethod) {
        await variantConfig.updateCategoryItemMethod(formState.editingId, { ...formState.formFields, updated_by: currentUser?.id });
        showSuccessToast("Category item updated");
      } else if (variantConfig?.createCategoryItemMethod && formState.editingParentId) {
        await variantConfig.createCategoryItemMethod({
          ...formState.formFields,
          category_id: formState.editingParentId,
          created_by: currentUser?.id,
        });
        showSuccessToast("Category item created");
      }
      cancelEditing();
      await loadAllData();
    } catch (error: any) { showErrorToast(error?.message || "Failed to save category item"); }
  };

  const handleCategoryItemDelete = async (item: CategoryItemData) => {
    try {
      if (variantConfig?.deleteCategoryItemMethod) {
        await variantConfig.deleteCategoryItemMethod(item.id);
        showSuccessToast("Category item deleted");
        await loadAllData();
      }
    } catch (error: any) { showErrorToast(error?.message || "Failed to delete category item"); }
  };

  // ═══════════ SUBJECT HANDLERS ═══════════
  const handleSubjectClick = (sub: SubjectData) => {
    const fields: Record<string, any> = {};
    subjectFieldsConfig.forEach((f: any) => {
      fields[f.fieldName] = sub[f.fieldName] ?? f.defaultValue ?? "";
    });
    fields.semester_id = sub.semester_id;
    fields.category_id = sub.category_id ?? "";
    fields.category_item_id = sub.category_item_id ?? "";
    fields.electives = sub.electives || [];
    setFormState((prev) => ({
      ...prev,
      editingSection: "subject",
      editingId: sub.id,
      editingParentId: sub.semester_id,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleNewSubject = (semesterId: number) => {
    const fields = buildDefaultFieldValues(subjectFieldsConfig);
    fields.semester_id = semesterId;
    fields.category_id = "";
    fields.category_item_id = "";
    fields.electives = [];
    // Auto-increment priority based on existing subjects in this semester
    const maxPriority = formState.subjects
      .filter((s) => s.semester_id === semesterId)
      .reduce((max, sub) => Math.max(max, sub.priority ?? 0), 0);
    fields.priority = maxPriority + 1;
    setFormState((prev) => ({
      ...prev,
      editingSection: "subject",
      editingId: null,
      editingParentId: semesterId,
      formFields: fields,
      formFieldErrors: {},
      formFieldMessages: {},
    }));
  };

  const handleSubjectSave = async () => {
    const { errors, messages } = validateFields(subjectFieldsConfig, formState.formFields);
    setFormState((prev) => ({ ...prev, formFieldErrors: errors, formFieldMessages: messages }));
    if (Object.keys(errors).length > 0) { showValidationErrorToast(); return; }

    try {
      const { semester_id, category_id, category_item_id, electives, ...fieldData } = formState.formFields;
      const semId = Number(semester_id);
      if (!semId || isNaN(semId)) {
        showErrorToast("Please select a semester for this subject");
        return;
      }
      const payload: Record<string, any> = {
        ...fieldData,
        semester_id: semId,
        category_id: category_id ? Number(category_id) : null,
        category_item_id: category_item_id ? Number(category_item_id) : null,
        electives: electives || [],
      };

      if (formState.editingId && variantConfig?.updateSubjectMethod) {
        await variantConfig.updateSubjectMethod(formState.editingId, { ...payload, updated_by: currentUser?.id });
        showSuccessToast("Subject updated");
      } else if (variantConfig?.createSubjectMethod) {
        await variantConfig.createSubjectMethod({
          ...payload,
          component_id: Number(pageComponentId),
          program_structure_v2_id: formState.parentId,
          created_by: currentUser?.id,
        });
        showSuccessToast("Subject created");
      }
      cancelEditing();
      await loadAllData();
    } catch (error: any) { showErrorToast(error?.message || "Failed to save subject"); }
  };

  const handleSubjectDelete = async (sub: SubjectData) => {
    try {
      if (variantConfig?.deleteSubjectMethod) {
        await variantConfig.deleteSubjectMethod(sub.id);
        showSuccessToast("Subject deleted");
        await loadAllData();
      }
    } catch (error: any) { showErrorToast(error?.message || "Failed to delete subject"); }
  };

  // ═══════════ ELECTIVES ═══════════
  const handleElectivesUpdate = (electives: any[]) => {
    setFormState((prev) => ({ ...prev, formFields: { ...prev.formFields, electives } }));
  };

  // ═══════════ SAVE DISPATCH ═══════════
  const handleSave = () => {
    switch (formState.editingSection) {
      case "category": return handleCategorySave();
      case "categoryItem": return handleCategoryItemSave();
      case "semester": return handleSemesterSave();
      case "subject": return handleSubjectSave();
      default: return Promise.resolve();
    }
  };

  // ═══════════ DELETE DIALOG ═══════════
  const openDeleteDialog = (
    type: EditingSection,
    id: number,
    name: string,
    parentId?: number,
    setDeleteTarget?: (t: any) => void,
    setIsDeleteDialogOpen?: (v: boolean) => void,
  ) => {
    setDeleteTarget?.({ type, id, name, parentId });
    setIsDeleteDialogOpen?.(true);
  };

  const confirmDelete = async (
    deleteTarget: { type: EditingSection; id: number; name: string; parentId?: number } | null,
    setIsDeleteDialogOpen?: (v: boolean) => void,
    setDeleteTarget?: (t: any) => void,
  ) => {
    if (!deleteTarget) return;
    switch (deleteTarget.type) {
      case "category": await handleCategoryDelete({ id: deleteTarget.id, semester_id: deleteTarget.parentId || 0, name: deleteTarget.name, items: [] }); break;
      case "categoryItem": await handleCategoryItemDelete({ id: deleteTarget.id, name: deleteTarget.name }); break;
      case "semester": await handleSemesterDelete({ id: deleteTarget.id, name: deleteTarget.name, categories: [] }); break;
      case "subject": await handleSubjectDelete({ id: deleteTarget.id, name: deleteTarget.name, semester_id: deleteTarget.parentId || 0 }); break;
    }
    setIsDeleteDialogOpen?.(false);
    setDeleteTarget?.(null);
  };

  return {
    // Field handlers
    handleComponentFieldsUpdate,
    handleToolsUpdate,
    handleSkillsUpdate,
    handleFormFieldsUpdate,
    // Component
    handleComponentSave,
    // Semester
    handleSemesterClick,
    handleNewSemester,
    handleSemesterSave,
    handleSemesterDelete,
    // Category
    handleCategoryClick,
    handleNewCategory,
    handleCategorySave,
    handleCategoryDelete,
    // Category Item
    handleCategoryItemClick,
    handleNewCategoryItem,
    handleCategoryItemSave,
    handleCategoryItemDelete,
    // Subject
    handleSubjectClick,
    handleNewSubject,
    handleSubjectSave,
    handleSubjectDelete,
    // Electives
    handleElectivesUpdate,
    // Save dispatch
    handleSave,
    cancelEditing,
    // Delete helpers
    openDeleteDialog,
    confirmDelete,
  };
}
